import { useState, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

export function DocGenPanel({ onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const generate = useCallback(async (scope: 'file' | 'selection') => {
    const { openTabs, activeTabId } = useAppStore.getState()
    const activeTab = openTabs.find(t => t.id === activeTabId)
    if (!activeTab) { setError('No file open. Open a file first.'); return }
    if (!activeTab.content.trim()) { setError('File is empty.'); return }

    setLoading(true); setResult(null); setError(null)
    try {
      const res = await authFetch(apiUrl('/api/ai-tools/docs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: activeTab.content, language: activeTab.language, scope }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        setError(`Server error ${res.status}: ${text.slice(0, 200)}`)
        return
      }
      const data = await res.json()
      if (!data.documented) { setError('AI returned empty response. Try again.'); return }
      setResult(data.documented)
    } catch (e: any) {
      setError(`Request failed: ${e?.message ?? 'Unknown error'}. Is the server running?`)
    } finally { setLoading(false) }
  }, [])

  const apply = () => {
    if (!result) return
    const { openTabs, activeTabId, snapshotFile } = useAppStore.getState()
    const activeTab = openTabs.find(t => t.id === activeTabId)
    if (!activeTab) return
    snapshotFile(activeTab.path, activeTab.content)
    useAppStore.getState().markDirty(activeTab.id, result)
    onClose()
  }

  const activeTab = useAppStore(s => s.openTabs.find(t => t.id === s.activeTabId))

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>📝</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Generate Docs</span>
        <div style={{ flex: 1 }} />
        <button onClick={() => generate('file')} disabled={loading} style={btnStyle}>
          {loading ? 'Generating...' : '📝 Document File'}
        </button>
        {result && (
          <button onClick={apply} style={{ ...btnStyle, background: 'rgba(95,212,164,0.15)', color: 'var(--accent-2)' }}>
            ✓ Apply
          </button>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {error && (
          <div style={{ padding: '10px 14px', background: 'rgba(240,112,112,0.08)', borderBottom: '1px solid rgba(240,112,112,0.2)' }}>
            <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>⚠ {error}</div>
          </div>
        )}
        {loading && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            Generating documentation with AI... this may take a few seconds
          </div>
        )}
        {!result && !loading && !error && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            {activeTab ? `Generate JSDoc/docstrings for ${activeTab.name}` : 'Open a file first'}
          </div>
        )}
        {result && (
          <pre style={{ margin: 0, padding: 14, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {result}
          </pre>
        )}
      </div>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(124,110,245,0.3)',
  background: 'rgba(124,110,245,0.1)', color: 'var(--accent)',
  cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
}
